import { useCallback, useEffect } from "react";
import { useRecoilState } from "recoil";
import { message } from "antd";
import { DsaTagAssignRequestDto, DsaTagAssignmentDto, DsaTagDto } from "../generated/dashboard-client";
import DSAApi from "../api/DSAApi";
import { dsaTagAssignmentsState, dsaTagsLoadedState, dsaTagsState } from "../state/atoms";
import { handleServerError } from "../util/ErrorHandler";

export const DsaTagItemType = {
  Screen: "SCREEN",
  Playlist: "PLAYLIST",
  Schedule: "SCHEDULE",
  Upload: "UPLOAD"
} as const;

export type DsaTagItemKind = typeof DsaTagItemType[keyof typeof DsaTagItemType];

let pendingLoad: Promise<void> | null = null;

export function useDsaTagsLoader() {
  const [loaded, setLoaded] = useRecoilState(dsaTagsLoadedState);
  const [, setTags] = useRecoilState(dsaTagsState);
  const [, setAssignments] = useRecoilState(dsaTagAssignmentsState);

  useEffect(() => {
    if (loaded || pendingLoad) return;
    pendingLoad = Promise.all([DSAApi.getTags(), DSAApi.getTagAssignments()])
      .then(([tagsResp, assignmentsResp]) => {
        setTags(tagsResp.data);
        setAssignments(assignmentsResp.data);
        setLoaded(true);
      })
      .catch(e => handleServerError(e, "Failed to fetch tags."))
      .finally(() => {
        pendingLoad = null;
      });
  }, [loaded, setTags, setAssignments, setLoaded]);
}

export function useDsaTags() {
  const [tags, setTags] = useRecoilState(dsaTagsState);
  const [assignments, setAssignments] = useRecoilState(dsaTagAssignmentsState);

  const tagIdsFor = useCallback((itemType: DsaTagItemKind, itemId: string) =>
    assignments
      .filter(a => String(a.itemType) === itemType && a.itemId === itemId)
      .map(a => a.tagId!),
  [assignments]);

  const tagsFor = useCallback((itemType: DsaTagItemKind, itemId: string) => {
    const ids = tagIdsFor(itemType, itemId);
    return tags.filter(t => ids.includes(t.id!));
  }, [tags, tagIdsFor]);

  const matchesTagFilter = useCallback((itemType: DsaTagItemKind, itemId: string, selectedTagIds: number[]) => {
    if (selectedTagIds.length === 0) return true;
    return tagIdsFor(itemType, itemId).some(id => selectedTagIds.includes(id));
  }, [tagIdsFor]);

  const setItemTag = useCallback(async (itemType: DsaTagItemKind, itemId: string, tagId: number, checked: boolean) => {
    const previous = assignments;
    const others = assignments.filter(a => !(String(a.itemType) === itemType && a.itemId === itemId && a.tagId === tagId));
    setAssignments(checked ? [...others, { tagId, itemType: itemType as DsaTagAssignmentDto["itemType"], itemId }] : others);
    try {
      await DSAApi.assignTags({
        itemType: itemType as DsaTagAssignRequestDto["itemType"],
        itemIds: [itemId],
        addTagIds: checked ? [tagId] : [],
        removeTagIds: checked ? [] : [tagId]
      });
    } catch (e) {
      setAssignments(previous);
      handleServerError(e, "Failed to update tags.");
    }
  }, [assignments, setAssignments]);

  const createTag = useCallback(async (name: string): Promise<DsaTagDto | null> => {
    try {
      const resp = await DSAApi.createTag({ name });
      setTags(current => current.some(t => t.id === resp.data.id)
        ? current
        : [...current, resp.data].sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase())));
      return resp.data;
    } catch (e) {
      handleServerError(e, "Failed to create tag.");
      return null;
    }
  }, [setTags]);

  const deleteTag = useCallback(async (tagId: number) => {
    try {
      await DSAApi.deleteTag(tagId);
      setTags(current => current.filter(t => t.id !== tagId));
      setAssignments(current => current.filter(a => a.tagId !== tagId));
      message.success("Tag deleted");
    } catch (e) {
      handleServerError(e, "Failed to delete tag.");
    }
  }, [setTags, setAssignments]);

  return { tags, assignments, tagsFor, matchesTagFilter, setItemTag, createTag, deleteTag };
}
